import type { FeatureNode, MotiveDocument } from "./types";

export class GraphError extends Error {}

export function topologicalSort(nodes: readonly FeatureNode[]): FeatureNode[] {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const permanent = new Set<string>();
  const temporary = new Set<string>();
  const sorted: FeatureNode[] = [];

  const visit = (node: FeatureNode): void => {
    if (permanent.has(node.id)) return;
    if (temporary.has(node.id)) throw new GraphError(`Circular feature dependency involving “${node.name}”`);
    temporary.add(node.id);
    for (const inputId of node.inputNodeIds) {
      const input = byId.get(inputId);
      if (!input) throw new GraphError(`Feature “${node.name}” references a missing input`);
      visit(input);
    }
    temporary.delete(node.id);
    permanent.add(node.id);
    sorted.push(node);
  };

  for (const node of nodes) visit(node);
  return sorted;
}

export function getTerminalNodeIds(document: MotiveDocument): string[] {
  const consumed = new Set<string>();
  for (const node of document.nodes) {
    if (!node.enabled) continue;
    for (const input of node.inputNodeIds) consumed.add(input);
  }
  return document.nodes
    .filter((node) => node.enabled && node.visible && !consumed.has(node.id))
    .map((node) => node.id);
}

export function descendantsOf(nodes: readonly FeatureNode[], nodeId: string): Set<string> {
  const descendants = new Set<string>();
  let changed = true;
  while (changed) {
    changed = false;
    for (const node of nodes) {
      if (descendants.has(node.id)) continue;
      if (node.inputNodeIds.includes(nodeId) || node.inputNodeIds.some((id) => descendants.has(id))) {
        descendants.add(node.id);
        changed = true;
      }
    }
  }
  return descendants;
}

export function validateNodeInputs(node: FeatureNode): string | null {
  const count = node.inputNodeIds.length;
  if (["box", "cylinder", "sphere", "cone", "wedge"].includes(node.type)) {
    return count === 0 ? null : "Primitive features cannot have inputs";
  }
  if (node.type === "boolean") return count === 2 ? null : "Boolean features require exactly two inputs";
  return count === 1 ? null : `${node.name} requires exactly one input`;
}
